import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useUserRoleStore } from "entities/auth/stores/userRoleStore";
import { AdminRole } from "entities/auth/types";
import { useBrandQuery } from "entities/brands/queries/useBrandQuery";
import { useGamesData } from "entities/game/hooks/useGamesData";
import { useAuthenticatedPageMutation } from "entities/pages/mutations/useAuthenticatedPageMutation";
import { useDeletePageMutation } from "entities/pages/mutations/useDeletePageMutation";
import { useUnauthenticatedPageMutation } from "entities/pages/mutations/useUnauthenticatedPageMutation";
import { useUpdatePageMaintenanceModeMutation } from "entities/pages/mutations/useUpdatePageMaintenanceModeMutation";
import { useUpdatePageModuleStatusMutation } from "entities/pages/mutations/useUpdatePageModuleStatusMutation";
import { useUpdatePageStatusMutation } from "entities/pages/mutations/useUpdatePageStatusMutation";
import {
  useAllModulesByBrandQuery,
  getAllModulesByBrandQueryData,
  setAllModulesByBrandQueryData,
} from "entities/pages/queries/useAllModulesByBrandQuery";
import { usePagesListByBrandQuery } from "entities/pages/queries/usePagesListByBrandQuery";
import { useReorderPagesQuery } from "entities/pages/queries/useReorderPagesQuery";
import { Page, PageModuleStatus } from "entities/pages/types";
import { FC, PropsWithChildren, useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useDrawerState } from "shared/hooks/useDrawerState";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { useLocalSearch } from "shared/hooks/useLocalSearch";
import { useModalState } from "shared/hooks/useModalState";
import { createContext } from "shared/utils/context";
import { PagesColumnsProps } from "../types";
import { PagesPageType, PagesTabs } from "./types";

const [Provider, usePagesPage] = createContext<PagesPageType>({
  name: "PagesPageContext",
  hookName: "usePagesPage",
  providerName: "PagesPageProvider",
});

const PagesPageProvider: FC<PropsWithChildren> = ({ children }) => {
  const { id } = useParams();
  const { pathname: currentBrandPath } = useLocation();
  const pageDrawer = useDrawerState<string>();
  const brandId = String(id);
  const { roles } = useUserRoleStore();
  const [pageId, setPageId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<PagesTabs>(PagesTabs.Preview);
  const isAdmin =
    roles.includes(AdminRole.SuperAdmin) ||
    roles.includes(AdminRole.ContentAdmin);

  const {
    searchData: searchGamesData,
    isLoading: isLoadingGames,
    tableData: gamesList,
  } = useGamesData({
    isActive: [],
    size: 10,
  });

  const { data: allModulesList, isLoading: isLoadingAllModules } =
    useAllModulesByBrandQuery(brandId);

  const { mutate: updateModuleStatus } = useUpdatePageModuleStatusMutation({
    brandId,
    pageId,
  });

  const onUpdateModuleStatus = async ({
    status,
    moduleId,
    pageId,
  }: {
    status: PageModuleStatus;
    moduleId: string;
    pageId: string;
  }) => {
    setPageId(pageId);

    const currentModules = getAllModulesByBrandQueryData(brandId);

    if (currentModules && currentModules.length > 0) {
      const updatedModules = currentModules.map((module) =>
        module.id === moduleId ? { ...module, status } : module,
      );

      setAllModulesByBrandQueryData(brandId, updatedModules);
    }

    updateModuleStatus({ status, moduleId });
  };

  const { data: brandDetails, isPending: isPendingBrandDetails } =
    useBrandQuery(brandId);
  const { data: pageList = [], isLoading } = usePagesListByBrandQuery(brandId);
  const { mutate: mutateReorderPages, isPending } = useReorderPagesQuery();
  const {
    mutate: mutateAuthenticatedPage,
    isPending: isLoadingAuthorizedPage,
  } = useAuthenticatedPageMutation();
  const {
    mutate: mutateUnauthenticatedPage,
    isPending: isLoadingUnauthorizedPage,
  } = useUnauthenticatedPageMutation();

  const {
    mutate: onUpdatePageMaintenanceMode,
    isPending: isPendingPageMaintenanceMode,
  } = useUpdatePageMaintenanceModeMutation(brandId);

  const searchData = useLocalSearch({
    searchableFields: ["title"],
    initialData: pageList || [],
  });

  const searchModulesData = useLocalSearch({
    searchableFields: ["name"],
    initialData: allModulesList || [],
  });

  const regularDeleteModal = useModalState();
  const deleteDefaultPageModal = useModalState();
  const pageMaintenanceModeModal = useModalState();
  const brandMaintenanceModeModal = useModalState();
  const unpublishDefaultPageModal = useModalState();
  const brandMaintenanceModeDescription: InputHookResult = useInputState(
    "brandMaintenanceModeDescription",
    "",
  );
  const pageMaintenanceModeDescription: InputHookResult = useInputState(
    "pageMaintenanceModeDescription",
    "",
  );

  const { mutate: deletePage, isPending: isPendingDeletePage } =
    useDeletePageMutation(brandId);
  const { mutate: updatePageStatus, isPending: isPageStatusChanging } =
    useUpdatePageStatusMutation(brandId);

  useEffect(() => {
    if (isPendingBrandDetails) {
      return;
    }

    brandMaintenanceModeDescription.setValue(
      brandDetails?.brandMaintenanceModeDescription ?? "",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandDetails, isPendingBrandDetails]);

  useEffect(() => {
    if (pageMaintenanceModeModal.modalDetails) {
      pageMaintenanceModeDescription.setValue(
        pageMaintenanceModeModal.modalDetails?.maintenanceModeDescription ?? "",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageMaintenanceModeModal.modalDetails]);

  const handleOpenDeleteModal = (page: Page) => {
    if (page?.isDefaultForAuthenticated || page?.isDefaultForUnauthenticated) {
      return deleteDefaultPageModal.openModal();
    }

    regularDeleteModal.openModal(page);
  };

  const handleOpenPageMaintenanceModeModal = (page: Page) => {
    pageMaintenanceModeModal.openModal(page);
  };

  const onCloseDeleteModal = () => {
    regularDeleteModal.closeModal();
  };

  const handleDeletePage = () => {
    deletePage({
      pageId: regularDeleteModal.modalDetails?.id,
      pages: pageList,
    });
    onCloseDeleteModal();
  };

  const handleActiveStatusPage = (page: Page) => {
    if (
      !page.isActive &&
      (page.isDefaultForAuthenticated || page.isDefaultForUnauthenticated)
    ) {
      return unpublishDefaultPageModal.openModal();
    }

    updatePageStatus({ pageId: page.id, isActive: page.isActive });
  };

  const handlePageMaintenanceMode = (pageId: string, value: boolean) =>
    onUpdatePageMaintenanceMode(
      {
        isMaintenanceMode: value,
        maintenanceModeDescription: Boolean(value)
          ? pageMaintenanceModeDescription.input.value
          : "",
        pageId,
      },
      { onSuccess: () => pageMaintenanceModeModal.closeModal() },
    );

  const handleAuthenticatedPage = (pageId: string) =>
    mutateAuthenticatedPage({ brandId, pageId });

  const handleUnauthenticatedPage = (pageId: string) =>
    mutateUnauthenticatedPage({ brandId, pageId });

  const writeAccess = useRolePermission("brandsAndPages:write");
  const columnsParams: PagesColumnsProps = {
    currentLocation: currentBrandPath,
    handlePublishStatus: handleActiveStatusPage,
    handleOpenDeleteModal,
    handleOpenPageMaintenanceModeModal,
    pageDrawer,
    handleAuthenticatedPage,
    handleUnauthenticatedPage,
    readOnly: !writeAccess.allowed,
    handlePageMaintenanceMode,
    isAdmin,
  };

  const isLoadingPages =
    isLoading ||
    isPending ||
    isPageStatusChanging ||
    isPendingDeletePage ||
    isPendingBrandDetails ||
    isLoadingAuthorizedPage ||
    isLoadingGames ||
    isLoadingUnauthorizedPage ||
    isPendingPageMaintenanceMode;

  const value = {
    data: pageList,
    pageDrawer,
    gamesList,
    searchGamesData,
    handleAuthenticatedPage,
    handleUnauthenticatedPage,
    columnsParams,
    isLoadingPages,
    mutateReorderPages,
    deleteDefaultPageModal,
    unpublishDefaultPageModal,
    pageMaintenanceModeModal,
    brandMaintenanceModeModal,
    brandMaintenanceModeDescription,
    pageMaintenanceModeDescription,
    handleDeletePage,
    handlePageMaintenanceMode,
    isLoadingDeletePage: isPendingDeletePage,
    isLoadingPageMaintenanceMode: isPendingPageMaintenanceMode,
    regularDeleteModal,
    brandDetails,
    searchData: {
      value: searchData.value,
      onChange: searchData.onChange,
      filteredData: searchData.filteredData,
      isSearchActive: Boolean(searchData.value),
    },
    activeTab,
    setActiveTab,
    allModulesList: allModulesList || [],
    isLoadingAllModules,
    onUpdateModuleStatus,
    searchModulesData: {
      value: searchModulesData.value,
      onChange: searchModulesData.onChange,
      filteredData: searchModulesData.filteredData,
      isSearchActive: Boolean(searchModulesData.value),
    },
  };

  return <Provider value={value}>{children}</Provider>;
};

export { PagesPageProvider, usePagesPage };
